"use client";

import Image from "next/image";
import { signIn } from "next-auth/react";
import { cormorant, dmSans } from "@/config/fonts";
import { useEffect, useState } from "react";

export const LandingPage = () => {
    const [reason, setReason] = useState<string | null>(null);
    const [authError, setAuthError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        setReason(params.get("reason"));
        setAuthError(params.get("error"));
    }, []);

    const handleLogin = () => {
        setLoading(true);
        signIn("keycloak", { callbackUrl: "/seleccionar-perfil" });
    };

    return (
        <main
            className={`${dmSans.variable} ${cormorant.variable}`}
            style={{
                minHeight: "100vh",
                backgroundColor: "#0d1321",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                fontFamily: "var(--font-dm-sans), sans-serif",
            }}
        >
            {/* Radial glow */}
            <div
                className="landing-radial"
                style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: "min(900px, 100vw)",
                    height: "min(900px, 100vw)",
                    background: "radial-gradient(ellipse at center, rgba(16,74,165,0.18) 0%, rgba(16,74,165,0.06) 50%, transparent 72%)",
                    pointerEvents: "none",
                    zIndex: 0,
                }}
            />

            {/* Grid */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage:
                        "linear-gradient(rgba(16,74,165,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(16,74,165,0.05) 1px, transparent 1px)",
                    backgroundSize: "56px 56px",
                    pointerEvents: "none",
                    zIndex: 0,
                }}
            />

            {/* Corners */}
            {[
                { top: 32, left: 32 },
                { top: 32, right: 32 },
                { bottom: 32, left: 32 },
                { bottom: 32, right: 32 },
            ].map((pos, i) => (
                <div key={i} style={{ position: "absolute", ...pos, zIndex: 1, opacity: 0.2, width: 64, height: 64 }}>
                    <div style={{
                        position: "absolute", top: 0, left: 0, right: 0,
                        height: 1,
                        background: pos.right != null
                            ? "linear-gradient(270deg, #104aa5, transparent)"
                            : "linear-gradient(90deg, #104aa5, transparent)",
                    }} />
                    <div style={{
                        position: "absolute", top: 0, bottom: 0,
                        left: pos.right != null ? "auto" : 0,
                        right: pos.right != null ? 0 : "auto",
                        width: 1,
                        background: pos.bottom != null
                            ? "linear-gradient(0deg, #104aa5, transparent)"
                            : "linear-gradient(180deg, #104aa5, transparent)",
                    }} />
                </div>
            ))}

            {/* Content */}
            <div
                style={{
                    position: "relative",
                    zIndex: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    width: "100%",
                    padding: "40px 24px",
                }}
            >
                {/* Logo */}
                <div
                    className="landing-reveal-1"
                    style={{
                        position: "relative",
                        width: "clamp(120px, 16vw, 200px)",
                        height: "clamp(120px, 16vw, 200px)",
                        marginBottom: "clamp(28px, 4vw, 52px)",
                    }}
                >
                    <div
                        className="landing-orbit"
                        style={{
                            position: "absolute",
                            inset: "-14%",
                            borderRadius: "50%",
                            border: "1px solid rgba(16,74,165,0.32)",
                        }}
                    >
                        <div style={{
                            position: "absolute",
                            top: -4, left: "50%",
                            transform: "translateX(-50%)",
                            width: 7, height: 7,
                            borderRadius: "50%",
                            background: "#104aa5",
                            boxShadow: "0 0 10px 2px rgba(16,74,165,0.8)",
                        }} />
                    </div>
                    <div
                        className="landing-orbit-reverse"
                        style={{
                            position: "absolute",
                            inset: "-5%",
                            borderRadius: "50%",
                            border: "1px dashed rgba(16,74,165,0.18)",
                        }}
                    />
                    <div
                        className="landing-float"
                        style={{
                            position: "absolute", inset: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                    >
                        <div style={{
                            width: "82%", height: "82%",
                            borderRadius: "50%",
                            background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <Image
                                src="/img/logosf.png"
                                alt="Colegio Albert Einstein"
                                width={130}
                                height={130}
                                style={{ objectFit: "contain", width: "75%", height: "75%" }}
                                priority
                            />
                        </div>
                    </div>
                </div>

                {/* Title */}
                <h1
                    className="landing-shimmer-text landing-reveal-2"
                    style={{
                        fontFamily: "var(--font-cormorant), Georgia, serif",
                        fontSize: "clamp(1.8rem, 4.5vw, 3.8rem)",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                        lineHeight: 1.1,
                        marginBottom: "clamp(10px, 1.5vw, 18px)",
                    }}
                >
                    Colegio Albert Einstein
                </h1>

                {/* Divider */}
                <div
                    className="landing-reveal-3"
                    style={{
                        display: "flex", alignItems: "center", gap: 12,
                        marginBottom: "clamp(10px, 1.5vw, 18px)",
                        width: "100%", maxWidth: "min(360px, 60vw)",
                    }}
                >
                    <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, rgba(16,74,165,0.55))" }} />
                    <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#104aa5", boxShadow: "0 0 6px rgba(16,74,165,0.7)" }} />
                    <div style={{ flex: 1, height: 1, background: "linear-gradient(270deg, transparent, rgba(16,74,165,0.55))" }} />
                </div>

                {/* Subtitle */}
                <p
                    className="landing-reveal-3"
                    style={{
                        fontFamily: "var(--font-dm-sans), sans-serif",
                        fontSize: "clamp(0.6rem, 1vw, 0.78rem)",
                        fontWeight: 300,
                        letterSpacing: "0.26em",
                        textTransform: "uppercase",
                        color: "rgba(168,196,240,0.6)",
                        marginBottom: "clamp(32px, 5vw, 56px)",
                    }}
                >
                    Sistema de Gestión Académica
                </p>

                {/* Alerts */}
                {reason === "session_expired" && (
                    <div className="landing-reveal-4" style={{
                        background: "rgba(234,179,8,0.07)",
                        border: "1px solid rgba(234,179,8,0.22)",
                        borderRadius: 6, padding: "10px 20px",
                        marginBottom: 20, width: "100%",
                        maxWidth: "min(420px, 90vw)",
                    }}>
                        <p style={{ color: "#fde68a", fontSize: "0.8rem" }}>
                            Tu sesión expiró. Por favor inicia sesión nuevamente.
                        </p>
                    </div>
                )}
                {authError && (
                    <div className="landing-reveal-4" style={{
                        background: "rgba(239,68,68,0.07)",
                        border: "1px solid rgba(239,68,68,0.2)",
                        borderRadius: 6, padding: "10px 20px",
                        marginBottom: 20, width: "100%",
                        maxWidth: "min(420px, 90vw)",
                    }}>
                        <p style={{ color: "#fca5a5", fontSize: "0.8rem" }}>
                            Error al iniciar sesión. Verifique su conexión e intente nuevamente.
                        </p>
                    </div>
                )}

                {/* CTA */}
                <button
                    onClick={handleLogin}
                    disabled={loading}
                    className="landing-reveal-4 landing-glow"
                    style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "clamp(13px, 1.5vw, 18px) clamp(28px, 4vw, 56px)",
                        background: "linear-gradient(135deg, rgba(16,74,165,0.88) 0%, rgba(9,43,91,0.95) 100%)",
                        border: "1px solid rgba(16,74,165,0.55)",
                        borderRadius: 3,
                        cursor: loading ? "wait" : "pointer",
                        fontFamily: "var(--font-dm-sans), sans-serif",
                        fontSize: "clamp(0.68rem, 0.9vw, 0.82rem)",
                        fontWeight: 500,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: "#dce8ff",
                        transition: "transform 0.18s ease",
                        marginBottom: "clamp(32px, 5vw, 56px)",
                        opacity: loading ? 0.65 : 1,
                    }}
                    onMouseEnter={e => { if (!loading) (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-3px)"; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)"; }}
                >
                    {loading ? (
                        <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: "orbit 0.9s linear infinite" }}>
                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                            </svg>
                            <span>Redirigiendo...</span>
                        </>
                    ) : (
                        <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                                <polyline points="10 17 15 12 10 7" />
                                <line x1="15" y1="12" x2="3" y2="12" />
                            </svg>
                            <span>Acceder al Sistema</span>
                        </>
                    )}
                </button>

                {/* Footer */}
                <div className="landing-reveal-5" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: "clamp(0.58rem, 0.7vw, 0.68rem)", letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(168,196,240,0.28)" }}>
                        Año Lectivo {new Date().getFullYear()}
                    </span>
                    <span style={{ color: "rgba(16,74,165,0.4)", fontSize: "0.4rem" }}>◆</span>
                    <span style={{ fontSize: "clamp(0.58rem, 0.7vw, 0.68rem)", letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(168,196,240,0.28)" }}>
                        Acceso Restringido
                    </span>
                </div>
            </div>
        </main>
    );
};
